import { useState, useEffect, useCallback } from "react";
import { dashboardAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { TrendingUp, Share2, Download, Copy, X, Trophy } from "lucide-react";
import { toast } from "sonner";

const CampaignProgressCard = () => {
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [shareOpen, setShareOpen] = useState(false);
    const [imageUrl, setImageUrl] = useState("");
    const [imageBlob, setImageBlob] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);

    const loadProgress = useCallback(async () => {
        try {
            const r = await dashboardAPI.progress();
            setProgress(r.data);
        } catch (e) {
            // Non-critical — card just hides if data is unavailable
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadProgress(); }, [loadProgress]);

    useEffect(() => () => { if (imageUrl) URL.revokeObjectURL(imageUrl); }, [imageUrl]);

    const openShare = async () => {
        setShareOpen(true);
        if (imageUrl) return;
        setImageLoading(true);
        try {
            const r = await dashboardAPI.milestoneCard();
            const blob = new Blob([r.data], { type: "image/png" });
            setImageBlob(blob);
            setImageUrl(URL.createObjectURL(blob));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not build milestone card");
            setShareOpen(false);
        } finally {
            setImageLoading(false);
        }
    };

    const downloadCard = () => {
        if (!imageUrl) return;
        const a = document.createElement("a");
        a.href = imageUrl;
        a.download = `creditfix-milestone-${new Date().toISOString().slice(0, 10)}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const copyShareCaption = () => {
        const headline = progress?.headline?.text || "My credit-repair campaign";
        const caption = `${headline} — ${progress?.percent || 0}% through my credit-repair campaign with CreditFix AI.`;
        navigator.clipboard.writeText(caption).then(
            () => toast.success("Caption copied — paste it on your favorite platform"),
            () => toast.error("Could not copy"),
        );
    };

    const shareNative = async () => {
        if (!imageBlob || !navigator.share) {
            downloadCard();
            return;
        }
        try {
            const file = new File([imageBlob], "creditfix-milestone.png", { type: "image/png" });
            const headline = progress?.headline?.text || "My credit-repair campaign";
            await navigator.share({
                title: "CreditFix AI milestone",
                text: `${headline} — ${progress?.percent || 0}% through my campaign.`,
                files: [file],
            });
        } catch (e) {
            if (e.name !== "AbortError") {
                downloadCard();
            }
        }
    };

    if (loading) return null;
    if (!progress || progress.total === 0) return null;

    const percent = progress.percent || 0;

    return (
        <>
            <Card className="border-border overflow-hidden" data-testid="campaign-progress-card">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-serif flex items-center gap-2">
                        <Trophy className="h-5 w-5 text-primary" />
                        Campaign Progress
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Big percent + bar */}
                    <div>
                        <div className="flex items-end justify-between mb-2">
                            <p className="text-5xl font-bold font-mono leading-none" data-testid="campaign-percent">
                                {percent}<span className="text-2xl text-muted-foreground">%</span>
                            </p>
                            <div className="text-right text-xs text-muted-foreground">
                                <p className="font-mono text-foreground"><span className="font-semibold">{progress.resolved}</span> resolved</p>
                                <p className="font-mono text-foreground"><span className="font-semibold">{progress.in_flight}</span> in flight</p>
                                <p className="font-mono text-foreground"><span className="font-semibold">{progress.pending}</span> pending</p>
                            </div>
                        </div>
                        <div className="h-2 rounded-full bg-muted overflow-hidden">
                            <div
                                className="h-full rounded-full bg-accent transition-all duration-700"
                                style={{ width: `${percent}%` }}
                            />
                        </div>
                    </div>

                    {/* Headline milestone */}
                    {progress.headline && (
                        <div className="rounded-lg bg-muted/50 p-3 flex items-start gap-2">
                            <TrendingUp className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-foreground leading-snug">
                                {progress.headline.text}
                            </p>
                        </div>
                    )}

                    <Button
                        onClick={openShare}
                        className="w-full rounded-full btn-press"
                        data-testid="share-milestone-btn"
                    >
                        <Share2 className="h-4 w-4 mr-2" />
                        Share my milestone
                    </Button>
                </CardContent>
            </Card>

            <Dialog open={shareOpen} onOpenChange={setShareOpen}>
                <DialogContent className="sm:max-w-md" data-testid="milestone-share-modal">
                    <DialogHeader>
                        <DialogTitle className="font-serif">Share your win</DialogTitle>
                        <DialogDescription>
                            A 1080×1080 image — perfect for Instagram, Twitter, or texting a friend.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="rounded-lg overflow-hidden border border-border bg-muted/30 flex items-center justify-center min-h-[280px]">
                        {imageLoading || !imageUrl ? (
                            <div className="flex flex-col items-center gap-3 py-12">
                                <span className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
                                <p className="text-xs text-muted-foreground">Generating your card...</p>
                            </div>
                        ) : (
                            <img
                                src={imageUrl}
                                alt="Campaign milestone"
                                className="max-h-[360px] w-auto"
                                data-testid="milestone-preview-img"
                            />
                        )}
                    </div>

                    <DialogFooter className="gap-2 flex-row flex-wrap sm:flex-nowrap">
                        <Button variant="outline" onClick={copyShareCaption} disabled={!imageUrl} className="flex-1 rounded-full" data-testid="copy-caption-btn">
                            <Copy className="h-4 w-4 mr-2" />
                            Copy caption
                        </Button>
                        <Button variant="outline" onClick={downloadCard} disabled={!imageUrl} className="flex-1 rounded-full" data-testid="download-card-btn">
                            <Download className="h-4 w-4 mr-2" />
                            Download
                        </Button>
                        <Button onClick={shareNative} disabled={!imageUrl} className="flex-1 rounded-full btn-press" data-testid="share-card-btn">
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default CampaignProgressCard;
